import React, { useState, useEffect } from 'react';
import {
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaHeart,
  FaPlay,
  FaShoppingCart,
  FaCreditCard,
} from 'react-icons/fa';
// import { format } from 'date-fns'; // ✅ Import for date formatting
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './CourseSuggestion.css';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
// import Swal from 'sweetalert2';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import { getDisplayPrices } from '../../utils/priceUtils';

const CourseSuggestion = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const { token, profile } = useAuth();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(4); // Items to display per slide
  const [courses, setCourses] = useState([]);
  const [wishlist, setWishlist] = useState([]); // Track wishlist items
  const [cart, setCart] = useState([]); // Track cart items
  const [buyNow, setBuyNow] = useState([]); // ✅ Track Buy Now items
  const [userName, setUserName] = useState('Learner');
  const { profileLoading } = useAuth();
  const [profilePicture, setProfilePicture] = useState();
  const [filteredCourses, setFilteredCourses] = useState([]);

  // Adjust itemsPerSlide dynamically based on screen size
  useEffect(() => {
    const updateItemsPerSlide = () => {
      if (window.innerWidth <= 320) setItemsPerSlide(1);
      else if (window.innerWidth <= 480) setItemsPerSlide(1);
      else if (window.innerWidth <= 768) setItemsPerSlide(2);
      else setItemsPerSlide(4);
    };

    window.addEventListener('resize', updateItemsPerSlide);
    updateItemsPerSlide();

    return () => window.removeEventListener('resize', updateItemsPerSlide);
  }, []);

  // Fetch courses and cart/wishlist data
  useEffect(() => {
    const fetchCoursesAndData = async () => {
      try {
        // Fetch all published courses
        const coursesResponse = await axios.get(api.getAllCourses);
        const publishedCourses = (coursesResponse.data.courses || []).filter(course => course.published);

        // Fetch purchased courses
        const purchasedCoursesResponse = await axios.get(`${api.getPurchasedCourses}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const purchasedCourses = purchasedCoursesResponse.data.courses;

        // Exclude purchased courses from the suggestion list
        const filteredCourses = publishedCourses.filter(course =>
          !purchasedCourses.some(purchasedCourse => purchasedCourse._id === course._id),
        );

        setCourses(filteredCourses || []);

        // Fetch cart and wishlist data
        const buyNowResponse = await axios.get(`${api.getCartAndWishlist}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCart(Array.isArray(buyNowResponse.data?.cart) ? buyNowResponse.data.cart : []);
        setWishlist(Array.isArray(buyNowResponse.data?.wishlist) ? buyNowResponse.data.wishlist : []);
      } catch (error) {
        console.error('Error fetching courses or BuyNow data:', error);
      }
    };

    fetchCoursesAndData();
  }, [token]);

  // Filter courses based on search history
  useEffect(() => {
    if (profile && profile.searchHistory && profile.searchHistory.length > 0) {
      // Get the most recent search terms (up to 10)
      const recentSearches = profile.searchHistory.slice(0, 10);

      // Filter courses that match any of the recent search terms
      const matchedCourses = courses.filter(course => {
        return recentSearches.some(searchTerm => {
          const searchLower = searchTerm.toLowerCase();
          return (
            course.title.toLowerCase().includes(searchLower) ||
            course.description.toLowerCase().includes(searchLower) ||
            course.instructor.toLowerCase().includes(searchLower)
          );
        });
      });

      // Sort courses by relevance (number of search term matches)
      const sortedCourses = matchedCourses.sort((a, b) => {
        const aMatches = recentSearches.filter(term =>
          a.title.toLowerCase().includes(term.toLowerCase()) ||
          a.description.toLowerCase().includes(term.toLowerCase()) ||
          a.instructor.toLowerCase().includes(term.toLowerCase()),
        ).length;

        const bMatches = recentSearches.filter(term =>
          b.title.toLowerCase().includes(term.toLowerCase()) ||
          b.description.toLowerCase().includes(term.toLowerCase()) ||
          b.instructor.toLowerCase().includes(term.toLowerCase()),
        ).length;

        return bMatches - aMatches; // Sort in descending order
      });

      // Shuffle the sorted courses
      const shuffledCourses = [...sortedCourses].sort(() => Math.random() - 0.5);

      // Take only the first 10 courses
      setFilteredCourses(shuffledCourses.slice(0, 10));
    } else {
      setFilteredCourses([]);
    }
  }, [courses, profile]);

  // Slider navigation
  const nextSlide = () => {
    if ((currentSlide + 1) * itemsPerSlide < courses.length) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleRatingChange = async (courseId, rating) => {
    try {
      await axios.put(
        api.submitRating(courseId),
        { rating },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update the course's average rating locally
      setCourses((prevCourses) =>
        prevCourses.map((course) => {
          if (course._id !== courseId) return course;
          const currentAvg = Number.isFinite(course.averageRating) ? course.averageRating : 0;
          const currentCount = Array.isArray(course.ratings) ? course.ratings.length : 0;
          const newAverage = (currentAvg * currentCount + rating) / (currentCount + 1);
          return { ...course, averageRating: Number.isFinite(newAverage) ? newAverage : rating };
        }),
      );
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  };

  // Handle adding a course to the cart
  const handleAddToCart = async (courseId) => {
    try {
      const response = await axios.post(
        `${api.addToCart}`,
        { courseId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCart([...cart, { _id: courseId, status: 2 }]); // Status 2 for cart
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };


  // Handle adding/removing a course to/from the wishlist
  const handleWishlistToggle = async (courseId) => {
    try {
      if (wishlist.some((item) => item._id === courseId)) {
        await axios.delete(`${api.addToWishlist}/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlist((prev) => prev.filter((item) => item._id !== courseId));
      } else {
        const response = await axios.post(
          `${api.addToWishlist}`,
          { courseId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setWishlist([...wishlist, { _id: courseId, status: 1 }]); // Status 1 for wishlist
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
  };

  useEffect(() => {
    if (!profileLoading && profile) {
      const name = profile.name || 'Learner';
      setUserName(name);
      setProfilePicture(
        getProfilePictureUrl(profile.profilePicture, api.img, defaultProfilePic, name),
      );
    }
  }, [profile, profileLoading]);

  // Handle the Buy Now action
  const handleBuyNow = async (courseId) => {
    try {
      await axios.post(
        `${api.addToBuyNow}`,
        { courseId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      navigate(`/checkout?courseIds=${courseId}`);
    } catch (error) {
      console.error('Error adding to Buy Now:', error);
    }
  };

  // If no search history or no filtered courses, don't render the component
  if (!profile?.searchHistory?.length || filteredCourses.length === 0) {
    return null;
  }

  return (
    <div className="course-suggestion-container">
      <div className="header-h1">
        <h3>Suggestions based on your recent searches</h3>
      </div>
      <div className="course-slider">
        <FaChevronLeft
          className={`slider-icon ${currentSlide === 0 ? 'disabled' : ''}`}
          onClick={prevSlide}
        />
        <div className="course-list">
          {filteredCourses
            .slice(currentSlide * itemsPerSlide, (currentSlide + 1) * itemsPerSlide)
            .map((course) => (
              <div className="course-card-wrapper" key={course._id}>
                <div className="suggestions-course-card">
                  <button
                    className="wishlist-button"
                    onClick={() => handleWishlistToggle(course._id)}
                  >
                    <FaHeart
                      className={`wishlist-icon ${wishlist.some((item) => item._id === course._id) ? 'filled' : ''
                      }`}
                    />
                  </button>

                  <div
                    onClick={() => navigate(`/courses/${course._id}`)}
                    className="course-card-image-wrapper"
                  >
                    {course.image && (
                      <img
                        src={`${api.img + course.image}`}
                        alt={course.title}
                        className="course-card-image"
                      />
                    )}
                    <div className="play-button-overlay">
                      <FaPlay className="play-icon" />
                    </div>
                  </div>
                  <div className="pricebox">
                    <p className="course-price">
                      {(() => {
                        const prices = getDisplayPrices(course);
                        // Check database values first, not calculated display prices
                        // A course is free if: isFree flag is set, OR offerPrice is 0, OR both prices are 0
                        const isCourseFree = course.isFree || 
                                           course.offerPrice === 0 ||
                                           (course.actualPrice === 0 && course.offerPrice === undefined);
                        return (
                          <>
                            {isCourseFree ? (
                              <span className="free-price">Free</span>
                            ) : (
                              `₹ ${prices.offerPrice}`
                            )}
                            {!isCourseFree && (
                              <span className="original-price">₹{prices.actualPrice}</span>
                            )}
                            {!isCourseFree && prices.offerPrice !== prices.actualPrice && (
                              <span className="discount" style={{ marginLeft: '8px' }}>
                                {Math.round(
                                  ((prices.actualPrice - prices.offerPrice) /
                                    prices.actualPrice) *
                                  100,
                                )}
                                % Off
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </p>
                  </div>

                  <div className="course-card-box">
                    <h3>{course.title}</h3>
                    <p> {course.instructor}, {course.description}</p>
                  </div>

                  <div className="course-rating">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <FaStar
                        key={index}
                        className={
                          index < Math.round(course.averageRating)
                            ? 'star filled'
                            : 'star'
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Hidden by default, revealed on hover */}
                <div className="course-details">


                  <div className="sug-button-container">
                    {cart.some((item) => item._id === course._id) ? (
                      <button
                        className="sug-go-to-cart"
                        onClick={() => navigate('/cart')}
                      >
                        <FaShoppingCart className="cart-icon" /> Go to Cart
                      </button>
                    ) : (
                      <button
                        className="sug-add-to-cart"
                        onClick={() => handleAddToCart(course._id)}
                      >
                        <FaShoppingCart /> Add to Cart
                      </button>
                    )}
                    <button
                      className="sug-buy-now"
                      onClick={() => handleBuyNow(course._id, course.price)}
                    >
                      <FaCreditCard /> Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}

        </div>
        <FaChevronRight
          className={`slider-icon ${(currentSlide + 1) * itemsPerSlide >= courses.length
            ? 'disabled'
            : ''
          }`}
          onClick={nextSlide}
        />
      </div>
    </div>
  );
};

export default CourseSuggestion;
